import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  CardContent,
  CardActions,
  Alert,
} from "@mui/material";
import PageTitle from "../../components/PageTitle/PageTitle";
import customHttp from "../../services/customHTTP";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import Swal from "sweetalert2";
import { useSite } from "../../context/siteContext";
import { SERVER_BASE_URL } from "../../config";
import { useParams } from "react-router-dom";
import Datagrid from "../../components/Datagrid/Datagrid";
import './Documents.css'

const Documents = () => {
	const [loading, setLoading] = useState(true);
	const [categories, setCategories] = useState([]);
	const [documentsData, setDocumentsData] = useState({});
	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedDocuments, setSelectedDocuments] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const { selectedSite, siteData } = useSite();
	const { machine_id } = useParams();
	const [selectedMachineId, setSelectedMachineId] = useState(null);
	const [selectedMachine, setSelectedMachine] = useState(null);

	useEffect(() => {
		const fetchMachineDetails = async () => {
			try {
				const response = await customHttp.post(`/machines/by-ids/`, { machines: [machine_id] });
				if (response.data.success) {
					setSelectedMachine(response.data.data[0]);
				}
			} catch (error) {
				console.log(error);
			}
		};
		if (machine_id) {
			fetchMachineDetails();
			setSelectedMachineId(machine_id);
		} else {
			setSelectedMachine(null);
			setSelectedMachineId(null);
		}
	}, [machine_id]);

	useEffect(() => {
		setLoading(true);
		const fetchDocuments = async () => {
			try {
				const siteId = siteData.find((i) => i.customer_site_number === selectedSite)?.site_id;
				if (!selectedSite || !siteId) {
					return;
				}

				// Fetch Categories
				const catResponse = await customHttp.post("/documents/getCategories", {
					site_id: siteId,
				});
				let newCategories = catResponse.data.map((category) => ({
					name: category.category,
				}));

				// Fetch Documents
				const reqBody = { site_id: siteId };
				const docResponse = await customHttp.post("/documents/getDocuments", reqBody);

				if (docResponse.data.success) {
					const files = docResponse.data.data.files;
					const databaseDocs = docResponse.data.data.database;

					let filteredFiles = files;
					let filteredCategories = newCategories;
					// If machine_id is present, filter the documents accordingly
					if (machine_id) {
						const filteredDocs = databaseDocs.filter(
							(doc) => doc.assigned_machines && doc.assigned_machines.includes(machine_id)
						);

						// Organize filtered documents by category
						filteredFiles = filteredDocs.reduce((acc, doc) => {
							if (!acc[doc.category]) {
								acc[doc.category] = [];
							}
							acc[doc.category].push({
								name: doc.name,
								path: files[doc.category]?.find((f) => f.name === doc.name)?.path || "",
							});
							return acc;
						}, {});

						// Filter categories based on available documents
						filteredCategories = newCategories.filter((cat) => filteredFiles[cat.name]?.length > 0);
					}

					// Create a map for quick lookup by file name, outside of the machine_id check
					const databaseMap = databaseDocs.reduce((acc, doc) => {
						acc[doc.name] = {
							description: doc.description,
							assigned_machines: doc.machine_details || [],
						};
						return acc;
					}, {});

					// Enhance the filtered files with descriptions and machine details
					filteredFiles = Object.keys(filteredFiles).reduce((acc, category) => {
						acc[category] = filteredFiles[category].map((file) => {
							const docDetails = databaseMap[file.name] || {};
							return {
								...file,
								description: docDetails.description || "No Description available",
								assigned_machines: docDetails.assigned_machines || [],
							};
						});
						return acc;
					}, {});
					setCategories(filteredCategories);
					setSelectedCategory(filteredCategories.length > 0 ? filteredCategories[0].name : null);
					setDocumentsData(filteredFiles);
					setSelectedDocuments(filteredFiles[filteredCategories[0]?.name] || []);
				} else {
					console.error("Error fetching documents:", docResponse);
				}
			} catch (err) {
				console.error("Error fetching documents:", err);
			} finally {
				setLoading(false);
			}
		};

		const fetchMachineDetails = async () => {
			try {
				const response = await customHttp.post(`/machines/by-ids/`, { machines: [machine_id] });
				if (response.data.success) {
					setSelectedMachine(response.data.data[0]);
				}
			} catch (error) {
				console.log(error);
			}
		};
		if (machine_id) {
			console.log("Machine ID: ", machine_id);
			fetchMachineDetails();
			setSelectedMachineId(machine_id);
			fetchDocuments();
		} else {
			fetchDocuments();
		}
	}, [selectedSite, machine_id]);

	const handleCategoryClick = (categoryId) => {
		setSelectedCategory(categoryId);
		setSelectedDocuments(documentsData[categoryId]);
	};

	const handleSearchChange = (value) => {
		setSearchTerm(value);
	};

	const filteredDocuments = selectedDocuments
		.filter((doc) => doc.name.toLowerCase().includes(searchTerm.toLowerCase()))
		.map((doc, index) => ({
			...doc,
			id: index, // Ensures unique id
		}));

	const columns = [
		{ field: "name", headerName: "Document Name", flex: 1 },
		{
			field: "description",
			headerName: "Description",
			flex: 2,
			cellClassName: (params) => {
				return params.value === "No Description available" ? "gray-text" : "";
			},
		},
		{
			field: "assigned_machines",
			headerName: "Assigned Machine",
			flex: 2,
			valueGetter: (params) => {
				const assignedMachines = params;
				console.log(assignedMachines);
				if (assignedMachines && Array.isArray(assignedMachines) && assignedMachines.length > 0) {
					// Map through the array and extract all machine names, then join them with a comma
					return (
						assignedMachines.map((machine) => machine?.machine_name).join(", ") || "No Machine(s) assigned"
					);
				}
				return "No Machine(s) assigned";
			},
			cellClassName: (params) => {
				return params.value === "No Machine(s) assigned" ? "gray-text" : "";
			},
		},
		{
			field: "actions",
			headerName: "Actions",
			flex: 1,
			renderCell: (params) => (
				<>
					{params.row.path.toLowerCase().endsWith(".pdf") && (
						<IconButton
							size="small"
							color="primary"
							onClick={() => window.open(`${SERVER_BASE_URL}/${params.row.path}`)}>
							<PictureAsPdfIcon />
						</IconButton>
					)}
					<IconButton
						size="small"
						color="secondary"
						onClick={async () => {
							try {
								const response = await customHttp.get(`documents/downloadDocument/${params.row.name}`, {
									responseType: "blob",
								});

								const blob = new Blob([response.data], {
									type: "application/octet-stream",
								});

								const link = document.createElement("a");
								link.href = URL.createObjectURL(blob);
								link.download = params.row.name;
								document.body.appendChild(link);
								link.click();
								document.body.removeChild(link);

								console.log("File download initiated.");
							} catch (error) {
								Swal.fire({
									icon: "error",
									title: "Error",
									text: "Failed to download file. Please try again.",
								});
							}
						}}>
						<DownloadIcon />
					</IconButton>
				</>
			),
		},
	];

	return (
		<Box
			sx={{
				padding: "10px 40px 40px 40px",
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-start",
				height: "100%",
				width: "100%",
				minHeight: "60vh",
				maxHeight: "100vh",
				overflowY: "auto",
			}}>
			<PageTitle
				title={
					selectedMachineId && selectedMachine
						? `Documents:  ${selectedMachine.machine_number} | ${selectedMachine.machine_name}`
						: "Documents"
				}
			/>

			<Box
				sx={{
					display: "flex",
					width: "100%",
					marginTop: "20px",
					height: "100%",
				}}>
				<Box
					sx={{
						width: { xs: "30%", sm: "15%" },
						borderRight: "2px solid #ddd",
						paddingRight: "20px",
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-start",
						gap: "10px",
					}}>
					<ContextFilterAndSort
						showSearch
						showSort={false}
						searchValue={searchTerm}
						onSearchChange={handleSearchChange}
					/>
					<List sx={{ width: "100%", padding: 0 }}>
						{categories.map((category) => {
							const categoryDocuments = documentsData[category.name];
							const filteredCategoryDocuments = categoryDocuments?.filter((doc) =>
								doc.name.toLowerCase().includes(searchTerm.toLowerCase())
							);
							return (
								<ListItem
									button
									key={category.name}
									onClick={() => handleCategoryClick(category.name)}
									selected={category.name === selectedCategory}
									sx={{
										padding: "12px 20px",
										borderRadius: "6px",
										"&:hover": {
											backgroundColor: "#f0f0f0",
											transition: "background-color 0.3s ease, color 0.3s ease",
										},
										backgroundColor: category.name === selectedCategory ? "#e3f2fd" : "transparent",
										color: category.name === selectedCategory ? "#1976d2" : "#000",
										transition: "background-color 0.3s ease, color 0.3s ease",
										cursor: "pointer",
									}}>
									<ListItemText
										primary={
											<>
												{category.name}
												<span
													style={{
														fontSize: "0.8em",
														marginLeft: "8px",
														color: "#888",
													}}>
													({filteredCategoryDocuments?.length})
												</span>
											</>
										}
										sx={{
											color: category.name === selectedCategory ? "#1976d2" : "inherit",
											fontWeight: category.name === selectedCategory ? "bold" : "normal",
										}}
									/>
								</ListItem>
							);
						})}
					</List>
				</Box>

				<Box sx={{ width: "85%", paddingLeft: "20px", paddingTop: "20px" }}>
					<Typography variant="h6" sx={{ marginBottom: 1, fontWeight: "bold" }}>
						{categories.find((cat) => cat.name === selectedCategory)?.name}
					</Typography>

					{filteredDocuments.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "50vh",
								width: "100%",
							}}>
							<Alert severity="info">
								{selectedMachineId && selectedMachine
									? "No Documents assigned to this machine"
									: "No Documents in Category"}
							</Alert>
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: "16px",
								justifyContent: "flex-start",
							}}>
							<Datagrid
								data={filteredDocuments}
								columns={columns}
								rowIdField="id"
								pageSize={10}
								showSearchBar={true}
								rowClick={true}
								onRowClick={(params) => window.open(`${SERVER_BASE_URL}/${params.row.path}`)}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default Documents;
